import { useState } from 'react';
import { showError, showSuccess } from '@/utils/toast';

interface UseImageUploaderOptions {
  targetWidth?: number;
  targetHeight?: number;
  onImageReady?: (imageData: { url: string; file: File }) => void;
}

interface CropParams {
  x: number;
  y: number;
  width: number;
  height: number;
  imageData: string;
}

export const useImageUploader = (options: UseImageUploaderOptions = {}) => {
  const { targetWidth = 616, targetHeight = 353, onImageReady } = options;

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // URL-ből kép letöltése
  const downloadFromUrl = async (url: string): Promise<string | null> => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch('/api/images/download_from_url', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ url }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Nem sikerült letölteni a képet');
      }

      const data = await response.json();
      return data.imageData; // Base64 data URI
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Ismeretlen hiba történt';
      setError(message);
      showError(message);
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  // Kép közbevágása és feldolgozása
  const cropAndUpload = async (cropParams: CropParams): Promise<{ url: string; file: File } | null> => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch('/api/images/crop', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          imageData: cropParams.imageData,
          crop: {
            x: cropParams.x,
            y: cropParams.y,
            width: cropParams.width,
            height: cropParams.height,
          },
          width: targetWidth,
          height: targetHeight,
        }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Nem sikerült feldolgozni a képet');
      }

      const data = await response.json();

      // File objektum létrehozása az URL-ből
      const imageResponse = await fetch(data.url);
      const blob = await imageResponse.blob();
      const file = new File([blob], data.filename, { type: 'image/jpeg' });

      showSuccess('Kép sikeresen feldolgozva!');

      if (onImageReady) {
        onImageReady({ url: data.url, file });
      }

      return { url: data.url, file };
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Ismeretlen hiba történt';
      setError(message);
      showError(message);
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  // Kép közvetlen feltöltése (1:1, crop nélkül - auto-crop 16:9 arányhoz)
  const uploadDirect = async (imageData: string): Promise<{ url: string; file: File } | null> => {
    setIsLoading(true);
    setError(null);

    return new Promise((resolve) => {
      const img = new Image();
      img.onload = async () => {
        try {
          // Auto-crop: 16:9 arány középre központosítva
          let cropWidth = img.width;
          let cropHeight = img.width / (16 / 9); // 16:9 arány

          if (cropHeight > img.height) {
            cropHeight = img.height;
            cropWidth = img.height * (16 / 9);
          }

          const cropX = (img.width - cropWidth) / 2;
          const cropY = (img.height - cropHeight) / 2;

          const response = await fetch('/api/images/crop', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              imageData,
              crop: {
                x: cropX,
                y: cropY,
                width: cropWidth,
                height: cropHeight,
              },
              width: targetWidth,
              height: targetHeight,
            }),
          });

          if (!response.ok) {
            const data = await response.json();
            throw new Error(data.error || 'Nem sikerült feldolgozni a képet');
          }

          const data = await response.json();

          // File objektum létrehozása az URL-ből
          const imageResponse = await fetch(data.url);
          const blob = await imageResponse.blob();
          const file = new File([blob], data.filename, { type: 'image/jpeg' });

          showSuccess('Kép sikeresen feltöltve!');

          if (onImageReady) {
            onImageReady({ url: data.url, file });
          }

          setIsLoading(false);
          resolve({ url: data.url, file });
        } catch (err) {
          const message = err instanceof Error ? err.message : 'Ismeretlen hiba történt';
          setError(message);
          showError(message);
          setIsLoading(false);
          resolve(null);
        }
      };
      img.onerror = () => {
        const message = 'Nem sikerült betölteni a képet';
        setError(message);
        showError(message);
        setIsLoading(false);
        resolve(null);
      };
      img.src = imageData;
    });
  };

  // Fájl választás és feldolgozás (nem URL-ből)
  const handleLocalFile = (file: File): Promise<string | null> => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const result = e.target?.result as string;
        resolve(result); // Base64 data URI
      };
      reader.readAsDataURL(file);
    });
  };

  return {
    isLoading,
    error,
    downloadFromUrl,
    cropAndUpload,
    uploadDirect,
    handleLocalFile,
  };
};

export default useImageUploader;
